import { Button, Grid, Stack, SvgIcon, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import RolesBox from './components/RolesBox';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { useContext, useEffect, useState } from 'react';
import { SuccessResponse_1OfIEnumerable_1OfPersona, Persona, ModulePermission, PostPersonaRequest } from '../../../app/services/api/generated';
import useLogError from '../../../hooks/useLogError';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Plus } from '../../../assets';
import CreateUpdateRoleModal from './components/CreateUpdateRoleModal';
import { UserContext } from '../../../components/shared/useUser';

import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';

const ManageRoles = () => {
  const { getPersonas, getModulePermissions, createPersona } = useNswagClient();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [modulePermissions, setModulePermissions] = useState<ModulePermission[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showCreateRoleModal, setShowCreateRoleModal] = useState<boolean>(false);
  const [showCreateSuccessMessage, setShowCreateSuccessMessage] = useState<boolean>(false);
  const { t } = useTranslation('manageRoles');
  const { hasPermissionTo } = useContext(UserContext);
  const theme = useTheme();
  const { logError } = useLogError();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);

  const loadData = () => {
    setIsLoading(true);
    getPersonas()
      .then((result: SuccessResponse_1OfIEnumerable_1OfPersona) => {
        if (result?.data) {
          setPersonas(result.data);
        }
      })
      .catch((error) => logError(error))
      .finally(() => setIsLoading(false));
  };

  const handleFetchModulePermissions = () => {
    getModulePermissions()
      .then((result: ModulePermission[]) => {
        if (result) {
          setModulePermissions(result);
        }
      })
      .catch((error) => {
        logError(error);
      });
  };

  useEffect(() => {
    loadData();
    handleFetchModulePermissions();
  }, []);

  const handleCreateRole = (body: PostPersonaRequest) => {
    createPersona(body)
      .then(() => {
        setShowCreateSuccessMessage(true);
        loadData();
      })
      .catch((error) => {
        logError(error);
      });
  };

  return <PageArea>
    <Grid container>
      <Stack width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
        }}>
        <PageTitle subTitle={t('subtitle')}>{t('roles')}</PageTitle>
        <Button variant="primary"
          size="lg"
          disabled={!hasPermissionTo(['RoleManagementCreate'])}
          startIcon={<SvgIcon> <Plus fill='white' /> </SvgIcon>}
          onClick={() => setShowCreateRoleModal(true)}>
          {t('newRole')}
        </Button>
      </Stack>

      <RolesBox
        personas={personas}
        modulePermissions={modulePermissions}
        loadData={loadData}
        isLoading={isLoading}
        onCreate={() => setShowCreateRoleModal(true)}
      />
    </Grid>

    <CreateUpdateRoleModal
      openModal={showCreateRoleModal}
      onCancel={() => setShowCreateRoleModal(false)}
      persona={undefined}
      onConfirm={(requestBody: PostPersonaRequest) => handleCreateRole(requestBody)}
      modules={modulePermissions}
    />

    <MessagesSnackbar
      open={showCreateSuccessMessage}
      onClose={() => setShowCreateSuccessMessage(false)}
      message={'Role created successfully'}
      severity="success"
      duration={null}
    />
  </PageArea>;
};

export default ManageRoles;